import NestedTabNavigation from '@atoms/NestedTabNavigation';
import { useCallback, useMemo } from 'react';
import TeamGeneralSettings from './TeamGeneralSettings';
import LoadingPaper from '@atoms/LoadingPaper';
import { isTeamCoach, isTeamOwner } from '@utils/shared-utils';
import { Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getDefaultNewTeam, TeamForm } from '@components/teamCreation/CreateTeamUtils';
import TeamPaymentHistory from './TeamPaymentHistory';
import TeamPaymentConfig from './TeamPaymentConfig';
import Wrestlers from '@pages/Wrestlers/Wrestlers';
import useTeams from '@hooks/useTeams';
import useStripeAccount from '@hooks/useStripeAccount';
import Parents from './Parents';
import { updateTeam } from '@modules/supabase/teams';
import { toast } from 'react-toastify';
import useUser from '@hooks/useUser';
import Coaches from '@pages/Coaches/Coaches';

export default function TeamCoachView() {
    const { loading, role, selectedTeam } = useTeams();
    const isCoach = isTeamCoach(role);
    const isOwner = isTeamOwner(role);
    const currentUser = useUser();

    const { stripeInfo } = useStripeAccount();
    const formMethods = useForm<TeamForm>({
        defaultValues: getDefaultNewTeam(
            selectedTeam,
            stripeInfo?.detailsSubmitted,
            currentUser.user?.id,
        ),
        mode: 'all',
    });

    const tabs = useMemo(() => {
        return [
            {
                tabName: 'Info',
                renderFn: () => <TeamGeneralSettings />,
            },
            {
                tabName: 'Wrestlers',
                renderFn: () => <Wrestlers />,
            },
            {
                tabName: 'Parents',
                renderFn: () => <Parents editable={isOwner} />,
            },
            ...(isOwner
                ? [
                      {
                          tabName: 'Coaches & Admins',
                          renderFn: () => <Coaches />,
                      },
                      {
                          tabName: 'Payment Config',
                          renderFn: () => <TeamPaymentConfig />,
                      },
                      {
                          tabName: 'Payment History',
                          renderFn: () => <TeamPaymentHistory />,
                      },
                      //   FIXME: FailedPayments is using the Event Context, but it should be using the Team Context
                      //   {
                      //       tabName: 'Failed Registrations',
                      //       renderFn: () => <FailedPayments />,
                      //   },
                  ]
                : []),
        ];
    }, [isOwner]);

    const confirmTabChange = useCallback(() => {
        // if this is buggy, we can pull "dirtyFields" instead and cross-reference with the selected tab
        if (formMethods.formState.isDirty) {
            const discardChanges = confirm(
                'You have unsaved changes. Are you sure you want to leave?',
            );
            if (discardChanges) formMethods.reset();
            return discardChanges;
        }
        return true;
    }, [formMethods]);

    const onSubmit: SubmitHandler<TeamForm> = useCallback(
        async (values) => {
            console.log(`TeamCoachView onSubmit`, values);

            toast
                .promise(
                    updateTeam(
                        {
                            id: values.id,
                            name: values.name,
                            codeOfConduct: values.codeOfConduct,
                            imageUrl: values.imageUrl,
                            fee: values.fee,
                        },
                        `team [TeamCoachView]`,
                    ),
                    {
                        pending: `Updating ${values.name ? +`${values.name} ` : ''}team...`,
                        success: 'Team updated!',
                        error: 'Team update failed',
                    },
                )
                .then((updatedTeam) => {
                    if (updatedTeam) {
                        formMethods.reset({ ...values, ...updatedTeam }); // reset form to updated values
                    }
                });
        },
        [formMethods],
    );

    if (!loading && !isCoach) {
        return (
            <Typography>
                You are not authorized to view this page, Speak to an team owner
            </Typography>
        );
    }

    return (
        <LoadingPaper
            elevation={2}
            loading={loading}
            sx={{ padding: '1rem', margin: '1rem', elevation: 2 }}
        >
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                    <NestedTabNavigation tabs={tabs} confirmTabChange={confirmTabChange} />
                </form>
            </FormProvider>
        </LoadingPaper>
    );
}
